import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Switch } from '../../../components/Switch';
import { InputRange } from '../../../components/InputRange';
import { FormField } from '../../../components/FormField';
import { RoundedBox } from '../../../components/Shared/RoundedBox';
import { OverLay } from '../../../components/OverLay';
import { Input } from '../../../components/Input';
import { useGetSettings } from '../../../network';

export const Settings = ({ formik }) => {
    const { t } = useTranslation();
    const [range, setRange] = useState(10);

    const { data: settings = {} } = useGetSettings();

    const onChangeStoreType = (checked, type) => {
        formik.setFieldValue(type, checked);
    };

    const onChangeCharge = (e) => {
        const value = Number(e.target.value);
        if (!isNaN(value)) {
            formik.handleChange(e);
        }
    };

    const {
        has_delivery: hasDelivery,
        has_dine_in: hasDineIn,
        has_takeaway: hasTakeaway,
        service_charge_percent: serviceChargePercent,
        service_charge_round: serviceChargeRound,
        tips_allowed,
        order_notes_allowed,
        cutlery_allowed,
    } = formik.values;

    const isTipsAllowed = tips_allowed !== "0" && tips_allowed !== false;
    const isNotesAllowed = order_notes_allowed !== "0" && order_notes_allowed !== false;
    const isCutleryAllowed = cutlery_allowed !== "0" && cutlery_allowed !== false;

    return (
        <div className="grid grid-cols-2 grid-rows-2 grid-flow-col gap-4">
            <div className="flex flex-col gap-4 row-start-1">
                <RoundedBox className="col-span-2" title={t('System Function & Order Type')} description={t('Choose the functions and order types you would like to offer at this store')}>
                    <div className="flex gap-10 mt-6">
                        <FormField label="Dine-in" formFieldClassName="flex flex-col text-sm font-medium gap-4 justify-between">
                            <Switch
                                isChecked={hasDineIn}
                                onChange={(checked) => {
                                    onChangeStoreType(checked, 'has_dine_in');
                                }}
                            />
                        </FormField>
                        <FormField label="Delivery" formFieldClassName="flex flex-col text-sm font-medium gap-4 justify-between">
                            <Switch
                                isChecked={hasDelivery}
                                onChange={(checked) => {
                                    onChangeStoreType(checked, 'has_delivery');
                                }}
                            />
                        </FormField>
                        <FormField label="Pick-up" formFieldClassName="flex flex-col text-sm font-medium gap-4 justify-between">
                            <Switch
                                isChecked={hasTakeaway}
                                onChange={(checked) => {
                                    onChangeStoreType(checked, 'has_takeaway');
                                }}
                            />
                        </FormField>
                        {/*<FormField label="Visual Menu Only?" formFieldClassName="flex flex-col text-sm font-medium gap-4 justify-between">*/}
                        {/*    <Switch />*/}
                        {/*</FormField>*/}
                    </div>
                </RoundedBox>
                <RoundedBox className="col-span-2" title={t('Service charge')} description={t('Choose the service charge amount you would like to charge your customers for dine-in orders')}>
                    <div className="flex gap-2 mt-6">
                        <div className="w-1/2 relative">
                            <Input value={serviceChargePercent} name="service_charge_percent" onChange={onChangeCharge} className="px-4 py-2 bg-gray-50 w-full rounded-lg text-sm" />
                            <span className="absolute w-12 h-6 bg-purple-600 rounded-lg top-1.5 right-2 text-white flex items-center justify-center text-xs">%</span>
                        </div>
                    </div>

                    {<p className="mt-5 text-xs text-gray-700 mb-4">{t('You can also round up the service charge amount to your below')}</p>}
                    <div className="w-1/2 relative mt-4">
                        <Input value={serviceChargeRound} name="service_charge_round" onChange={onChangeCharge} className="px-4 py-2 bg-gray-50 w-full rounded-lg text-sm" />
                        <span className="absolute w-12 h-6 bg-purple-600 rounded-lg top-1.5 right-2 text-white flex items-center justify-center text-xs">{settings.currency?.title}</span>
                    </div>
                </RoundedBox>
                <RoundedBox className="col-span-2">
                    <div className="flex flex-col gap-6">
                        <div className="flex justify-between gap-4">
                            <div className="text-base font-bold min-w-[180px] w-[180px] flex justify-between gap-4">
                                {t('Tips')}

                                <Switch
                                    isChecked={isTipsAllowed}
                                    onChange={(checked) => {
                                        onChangeStoreType(checked, 'tips_allowed');
                                    }}
                                />
                            </div>

                            <p className="text-xs">{t('Enable waiter tips so your customers can reward your staff for their service!')}</p>
                        </div>
                        <div className="flex justify-between gap-4">
                            <div className="text-base font-bold min-w-[180px] w-[180px] flex justify-between gap-4">
                                {t('Order Notes')}
                                <Switch
                                    isChecked={isNotesAllowed}
                                    onChange={(checked) => {
                                        onChangeStoreType(checked, 'order_notes_allowed');
                                    }}
                                />
                            </div>

                            <p className="text-xs">{t('Enable order notes so your customers can leave comments and requests')}</p>
                        </div>
                        <div className="flex justify-between gap-4">
                            <div className="text-base font-bold min-w-[180px] w-[180px] flex justify-between gap-4">
                                {t('Store Cutlery')}
                                <Switch
                                    isChecked={isCutleryAllowed}
                                    onChange={(checked) => {
                                        onChangeStoreType(checked, 'cutlery_allowed');
                                    }}
                                />
                            </div>

                            <p className="text-xs">{t('Enabling this will allow your customers to add cutlery with their pickup or delivery orders')}</p>
                        </div>
                    </div>
                </RoundedBox>
            </div>

            <div className="flex flex-col gap-4 row-start-1">
                <OverLay text="On development stage" textClassName="text-xl">
                    <RoundedBox className="col-span-2" title="Time Slots" description="Set your time slots for takeaway & delivery orders">
                        <FormField label="Takeaway" labelStyle="mb-3 inline-flex">
                            <div className=" flex items-end gap-5">
                                <InputRange width="w-1/2" handleChange={setRange} value={range} valueOverThumb={true} />
                                <span className="text-sm font-medium text-gray-900">30 mins</span>
                            </div>
                        </FormField>
                        <FormField label="Delivery" labelStyle="mt-5 mb-3 inline-flex">
                            <div className=" flex items-end gap-5">
                                <InputRange width="w-1/2" handleChange={setRange} value={range} valueOverThumb={true} />
                                <span className="text-sm font-medium text-gray-900">30 mins</span>
                            </div>
                        </FormField>
                    </RoundedBox>

                    <RoundedBox
                        title="Takeaway Prep Time"
                        description="Set your preparation times for different levels of activity. This will help imporve pickup time estimations and reduce waiting times for customers that place an order via your branded app."
                    >
                        <FormField label="Quite" className="col-span-8 sm:col-start-4 sm:col-end-9">
                            <div className="w-10/12">
                                <InputRange handleChange={setRange} value={range} />
                            </div>
                        </FormField>
                        <FormField label="Normal" className="mt-5 col-span-8 sm:col-start-4 sm:col-end-9">
                            <div className="w-10/12">
                                <InputRange handleChange={setRange} value={range} />
                            </div>
                        </FormField>
                        <FormField label="Busy" className="mt-5 col-span-8 sm:col-start-4 sm:col-end-9">
                            <div className="w-10/12">
                                <InputRange handleChange={setRange} value={range} />
                            </div>
                        </FormField>
                    </RoundedBox>
                </OverLay>
            </div>
        </div>
    );
};
